import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HexColorPicker as ColorfulHexColorPicker } from 'react-colorful';
import { usePrevious } from '../../hooks/usePrevious';
import { expandShorthandHexColor, stripHexPrefix } from './color-picker.utils';
import styles from './HexColorInput.module.scss';

export interface HexColorInputProps {
  /** Current hex color value (with or without `#`). */
  color?: string;
  /** Called when the color changes (value without `#`). */
  onChange: (color: string) => void;
  /** Disable interaction. */
  disabled?: boolean;
}

export const HexColorInput = React.forwardRef<HTMLDivElement, HexColorInputProps>(
  ({ color = '', onChange, disabled }, ref) => {
    const [hex, setHex] = useState(stripHexPrefix(color));
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const previousColor = usePrevious(color);
    useEffect(() => {
      const expandedHexState = expandShorthandHexColor(hex);
      if (color !== previousColor && color !== expandedHexState) {
        setHex(color);
      }
    }, [color, previousColor, hex]);

    const handleChangeHex = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = stripHexPrefix(event.target.value);
        setHex(value);
        const expandedHex = expandShorthandHexColor(value);
        if (expandedHex.length === 6) {
          onChange(expandedHex);
        }
      },
      [onChange]
    );

    const handleBlur = useCallback(() => {
      setHex(color);
    }, [color]);

    const handlePickerChange = useCallback(
      (pickerColor: string) => {
        const stripped = stripHexPrefix(pickerColor);
        setHex(stripped);
        onChange(stripped);
      },
      [onChange]
    );

    const handleFocus = useCallback(() => {
      setIsPickerOpen(true);
    }, []);

    // Close picker on click outside
    useEffect(() => {
      if (!isPickerOpen) return;

      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setIsPickerOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isPickerOpen]);

    return (
      <div ref={ref} className={styles.container}>
        <div ref={containerRef}>
          <div className={styles.inputWrapper}>
            <span className={styles.prefix}>#</span>
            <input
              className={styles.input}
              type="text"
              maxLength={6}
              value={hex}
              onChange={handleChangeHex}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
            />
          </div>
          {isPickerOpen && (
            <div className={styles.pickerDropdown} onMouseDown={e => e.preventDefault()}>
              <ColorfulHexColorPicker color={`#${color}`} onChange={handlePickerChange} />
            </div>
          )}
        </div>
      </div>
    );
  }
);

HexColorInput.displayName = 'HexColorInput';
